<template>
  <section class="monitor-section" aria-labelledby="task-heading">
    <header class="section-heading">
      <div>
        <h2 id="task-heading" tabindex="-1">任务异常与记录</h2>
        <p>默认展示近 24 小时错误，可按状态查看相关任务；队列 Job 与业务任务分别计数。</p>
      </div>
    </header>
    <a-tabs v-model:active-key="store.tab"
      ><a-tab-pane key="agent" tab="Agent 任务" /><a-tab-pane
        key="creative"
        tab="创意任务" /><a-tab-pane key="queue" tab="队列 Job"
    /></a-tabs>
    <div class="filter-toolbar task-filters">
      <a-select
        v-if="store.tab === 'queue'"
        v-model:value="store.queueId"
        :options="queueOptions"
        aria-label="选择队列"
        class="queue-select"
      />
      <a-select v-model:value="store.query.scope" :options="scopeOptions" aria-label="任务范围" />
      <a-select v-model:value="store.query.status" :options="statusOptions" aria-label="任务状态" />
      <a-select
        v-if="store.tab !== 'queue'"
        v-model:value="store.query.kind"
        :options="kindOptions"
        aria-label="任务类型"
      />
      <a-input-search
        v-model:value="searchDraft"
        :placeholder="
          store.tab === 'queue' ? '完整 Job ID，回车查询' : '完整任务 / 会话 / 工作台 ID，回车查询'
        "
        aria-label="完整 ID 查询"
        allow-clear
        @search="search"
        class="id-search"
      />
    </div>
    <div class="task-summary">
      <span>{{ scopeDescription }}</span
      ><span v-if="store.listCurrent">共 {{ store.list?.total ?? 0 }} 条</span
      ><a-button v-if="store.query.search" type="link" size="small" @click="clearSearch"
        >清除 ID 查询</a-button
      >
    </div>
    <a-alert v-if="store.listError" :message="store.listError" type="error" show-icon
      ><template #action
        ><a-button size="small" @click="store.loadTasks">重试</a-button></template
      ></a-alert
    >
    <div
      v-if="store.listCurrent && Object.keys(store.list?.byStatus ?? {}).length"
      class="status-counts"
      aria-label="范围内状态统计"
    >
      <button
        v-for="(count, status) in store.list?.byStatus"
        :key="status"
        class="count-button"
        :aria-pressed="store.query.status === status"
        @click="store.query.status = String(status)"
      >
        {{ taskStatusLabels[status] ?? status }} <strong>{{ count }}</strong>
      </button>
    </div>
    <a-table
      :columns="columns"
      :data-source="rows"
      row-key="id"
      size="middle"
      :loading="store.listLoading && !store.listCurrent"
      :pagination="pagination"
      :scroll="{ x: 'max-content' }"
      @change="store.query.page = $event.current ?? 1"
    >
      <template #emptyText
        ><a-empty :description="store.listError ? '任务诊断暂不可用' : '当前范围没有匹配的任务'"
      /></template>
      <template #bodyCell="{ column, record }">
        <a-tag v-if="column.key === 'status'" :color="taskColor(record.status)">{{
          taskStatusLabels[record.status] ?? '未知'
        }}</a-tag>
        <span v-else-if="column.key === 'category'">{{
          kindLabels[record.category] ?? record.category
        }}</span>
        <template v-else-if="column.key === 'id'"
          ><button
            class="text-action task-id"
            :title="record.id"
            @click="store.openDetail(store.tab, record.id)"
          >
            {{ record.id }}</button
          ><small v-if="record.sessionId" :title="record.sessionId"
            >会话 {{ record.sessionId }}</small
          ><small v-else-if="record.taskId" :title="record.taskId"
            >关联任务 {{ record.taskId }}</small
          ><small v-if="record.errorCode" class="mobile-error">{{
            errorLabels[record.errorCode] ?? '未知错误分类'
          }}</small></template
        >
        <template v-else-if="column.key === 'progress'"
          ><span>{{
            record.metrics.progress !== undefined
              ? `${record.metrics.progress ?? '—'}%`
              : (taskStatusLabels[record.stage] ?? '—')
          }}</span
          ><small v-if="record.errorCode" class="error-code">{{
            errorLabels[record.errorCode] ?? '未知错误分类'
          }}</small></template
        >
        <template v-else-if="column.key === 'time'"
          ><time>{{ formatTime(record.updatedAt) }}</time
          ><small>创建 {{ formatTime(record.createdAt) }}</small></template
        >
        <a-button
          v-else-if="column.key === 'action'"
          type="link"
          size="small"
          @click="store.openDetail(store.tab, record.id)"
          >详情</a-button
        >
      </template>
    </a-table>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useAdminRuntimeMonitorStore } from '@/stores/admin-runtime-monitor.store';
import { errorLabels, formatTime, kindLabels, taskStatusLabels } from './monitor-format';
const store = useAdminRuntimeMonitorStore();
const searchDraft = ref(store.query.search);
watch(
  () => [store.tab, store.query.search],
  () => {
    searchDraft.value = store.query.search;
  },
);
const scopeOptions = [
  { value: 'active', label: '活跃任务' },
  { value: 'errors', label: '近 24 小时异常' },
  { value: 'all', label: '全部监控任务' },
];
const statuses = {
  agent: [
    'queued',
    'running',
    'waiting',
    'cancelling',
    'failed',
    'interrupted',
    'completed',
    'cancelled',
  ],
  creative: [
    'queued',
    'submitting',
    'processing',
    'downloading',
    'succeeded',
    'failed',
    'cancelled',
  ],
  queue: ['waiting', 'active', 'delayed', 'prioritized', 'waiting-children', 'failed', 'completed'],
};
const statusOptions = computed(() =>
  ['all', ...statuses[store.tab]].map((value) => ({ value, label: taskStatusLabels[value] })),
);
const kindOptions = computed(() =>
  [
    'all',
    ...(store.tab === 'agent'
      ? ['primary', 'delegate', 'dream', 'agent']
      : ['image', 'video', 'audio']),
  ].map((value) => ({ value, label: kindLabels[value] })),
);
const queueLabels: Record<string, string> = {
  'agent-runs': 'Agent 执行',
  'agent-session-reflection': '会话反思',
  'agent-memory-dream': '记忆整理',
  'agent-message-moderation': '消息审查',
  'workspace-deletions': '工作台删除',
  'coupon-issues': '发券',
  'commerce-maintenance': '结算维护',
  'connector-jobs': '连接器事件',
  'account-knowledge-ingestion': '账户知识入库',
  'creative-media': '创意生成',
};
const queueOptions = computed(() =>
  Object.entries(queueLabels).map(([value, label]) => ({ value, label })),
);
const rows = computed(() => (store.listCurrent ? (store.list?.items ?? []) : []));
const pagination = computed(() => ({
  current: store.query.page,
  pageSize: 20,
  total: store.listCurrent ? (store.list?.total ?? 0) : 0,
  showSizeChanger: false,
  hideOnSinglePage: true,
}));
const columns = [
  { title: '状态', key: 'status', width: 105 },
  { title: '类型', key: 'category', width: 120, responsive: ['md'] },
  { title: '任务 ID / 关联标识', key: 'id' },
  { title: '阶段 / 进度', key: 'progress', width: 160, responsive: ['md'] },
  { title: '最近更新 / 创建时间', key: 'time', width: 190, responsive: ['md'] },
  { title: '操作', key: 'action', width: 80, responsive: ['md'] },
];
const scopeDescription = computed(() =>
  store.query.search
    ? '按完整 ID 查询，跨越时间与活跃范围'
    : store.tab === 'queue' && store.query.scope === 'all'
      ? '全部保留 Job；已清理的记录不在范围内'
      : store.query.scope === 'all'
        ? '活跃任务与近 24 小时结束的任务'
        : store.query.scope === 'errors'
          ? '近 24 小时失败 / 中断记录'
          : '当前排队、执行或等待中的任务',
);
function search(): void {
  store.query.search = searchDraft.value.trim();
}
function clearSearch(): void {
  searchDraft.value = '';
  store.query.search = '';
}
function taskColor(status: string): string {
  return status === 'failed'
    ? 'error'
    : status === 'interrupted' || status === 'waiting'
      ? 'warning'
      : ['succeeded', 'completed'].includes(status)
        ? 'success'
        : 'default';
}
</script>
<style scoped>
.mobile-error {
  display: none;
}
@media (max-width: 767px) {
  .mobile-error {
    display: block;
  }
}
</style>
